import type { FilterState } from '../filter-menu';
import type { DataGridFilterOptionsState } from '@components/organisms';

interface IResolveFilterLabelsInput {
	selectedFilters: Record<string, FilterState>;
	filterStates?: DataGridFilterOptionsState;
}

/**
 * Fills in display labels for selected filter values that carry none, which happens when the
 * selection was hydrated from the URL (the query string only holds option values). Returns the
 * original map when nothing needs resolving so referential equality is preserved.
 */
export function resolveFilterLabels({
	selectedFilters,
	filterStates,
}: IResolveFilterLabelsInput): Record<string, FilterState> {
	if (!filterStates) {
		return selectedFilters;
	}

	let hasResolvedLabel = false;
	const resolved: Record<string, FilterState> = {};

	for (const [groupKey, filter] of Object.entries(selectedFilters)) {
		const options = filterStates[groupKey]?.options;
		if (!options?.length) {
			resolved[groupKey] = filter;
			continue;
		}

		const labels = new Map(filter.labels);
		const missingValues = Array.from(filter.values).filter(value => !labels.has(value));
		if (missingValues.length === 0) {
			resolved[groupKey] = filter;
			continue;
		}

		const labelsByValue = new Map(options.map(option => [String(option.value), option.label]));
		for (const value of missingValues) {
			const label = labelsByValue.get(String(value));
			if (label == null) {
				continue;
			}
			labels.set(value, label);
			hasResolvedLabel = true;
		}

		resolved[groupKey] = labels.size === filter.labels.size ? filter : { ...filter, labels };
	}

	return hasResolvedLabel ? resolved : selectedFilters;
}
